import { LOCAL_STORAGE_KEY, LOCAL_STORAGE_STEP_KEY } from "./strings";
import { clearTokens, readAccessToken, readUserDetails, removeUserDetails } from './auth-token-store';
function getCookieValue(name) {
    const cookieArr = document.cookie.split('; ');
    const selectedCookie = cookieArr.find((cookie) => {
        const [cookieName] = cookie.split('=');
        return cookieName === name;
    });
    return selectedCookie ? decodeURIComponent(selectedCookie.split('=')[1] || '') : null;
}
export const setEmailType = (data = {}) => {
    localStorage.setItem("email_type", JSON.stringify((data === null || data === void 0 ? void 0 : data.email_type) || "primary"));
};
export const setAccountType = (data = { account_type: '' }) => {
    localStorage.setItem("account_type", JSON.stringify((data === null || data === void 0 ? void 0 : data.account_type) || "organization"));
};
export const isVendor = () => {
    if (typeof window !== "undefined") {
        const item = localStorage.getItem("first_time");
        const first_time = item ? JSON.parse(item) : null;
        return first_time === "true";
    }
    return false;
};
export function getInitialLetter(name) {
    var _a;
    name = (_a = name === null || name === void 0 ? void 0 : name.toString()) === null || _a === void 0 ? void 0 : _a.trim();
    if (!name)
        return "";
    return name.charAt(0);
}
export const getUserProfileData = () => {
    if (typeof window === "undefined")
        return null;
    try {
        const data = readUserDetails() || localStorage.getItem("user-details");
        if (!data)
            return null;
        const parsed = JSON.parse(data);
        return parsed !== null && parsed !== void 0 ? parsed : null;
    }
    catch (error) {
        console.error("Failed to parse user-details:", error);
        return null;
    }
};
export const checkSuperAdmin = () => {
    var _a;
    const userData = getUserProfileData();
    const isAdmin = (_a = userData === null || userData === void 0 ? void 0 : userData.representative_details) === null || _a === void 0 ? void 0 : _a.is_super_admin;
    return isAdmin;
};
// Marktine-internal platform-admin claim, decoded straight off the
// crm-access-token JWT (top-level `is_super_admin`) — distinct from
// checkSuperAdmin() above, which reads this app's own org-level
// representative_details.is_super_admin (a customer's highest role within
// their own org). Same distinction fe-recruiter-core's Admin Console gate
// makes: platform ops vs. per-org admin are different roles that happen to
// share a name.
export const isPlatformSuperAdminFromToken = () => {
    if (typeof window === "undefined")
        return false;
    const token = getCookieValue("crm-access-token") || localStorage.getItem("crm-access-token") || readAccessToken();
    const payload = token === null || token === void 0 ? void 0 : token.split(".")[1];
    if (!payload)
        return false;
    try {
        const json = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
        const flag = json === null || json === void 0 ? void 0 : json.is_super_admin;
        return flag === 1 || flag === "1" || flag === true;
    }
    catch (_a) {
        return false;
    }
};
// Admin Console visibility: the platform-level JWT claim, and nothing else.
//
// This used to be `isPlatformSuperAdminFromToken() || checkSuperAdmin()`, on the
// reading that an org's own super-admin should reach the console too. IAM does
// not agree: it authorises the embed off the same top-level `is_super_admin`
// claim this decodes, so a customer who is super-admin *within their own org*
// (representative_details.is_super_admin, which checkSuperAdmin() reads) but
// carries `is_super_admin: 0` in the token got the tab, loaded the iframe, and
// was answered with ADMIN_CONSOLE_ACCESS_DENIED — i.e. offered a tab whose only
// possible content is "You do not have Admin Console access for this
// organisation". Reported from production on 2026-09-02.
//
// One authority, one gate: the claim IAM decides on is the claim that decides
// whether the entry point is drawn. The two super-admin notions genuinely differ
// (platform ops vs. highest role inside one org) and checkSuperAdmin() stays
// correct for the org-scoped UI that uses it — it was never the right test for
// this door.
export const canAccessAdminConsole = () => {
    return isPlatformSuperAdminFromToken();
};
export const getTeamCount = () => {
    var _a;
    const userData = getUserProfileData();
    const count = (_a = userData === null || userData === void 0 ? void 0 : userData.representative_details) === null || _a === void 0 ? void 0 : _a.reporting_to_me_count;
    return count;
};
export const truncatedName = (data) => {
    var _a, _b;
    const name = ((_b = (_a = data === null || data === void 0 ? void 0 : data.representative_details) === null || _a === void 0 ? void 0 : _a.full_name) === null || _b === void 0 ? void 0 : _b.trim()) || "User";
    const formatted = name.charAt(0).toUpperCase() + name.slice(1);
    return (formatted === null || formatted === void 0 ? void 0 : formatted.length) > 15
        ? `${formatted.slice(0, 15)}...`
        : formatted;
};
export const getUserAccountType = () => {
    if (typeof window !== "undefined") {
        const stored = localStorage.getItem("account_type");
        const accountType = JSON.parse(stored !== null && stored !== void 0 ? stored : '"organization"');
        return accountType;
    }
    return "organization";
};
export const getAddVendorVariablesPresent = () => {
    // localStorage.getItem can return string | null
    const first_time = localStorage.getItem("first_time");
    const redirect_to = localStorage.getItem("redirect_to");
    const create_password_token = localStorage.getItem("create_password_token");
    // Parse only if value exists
    const parsedFirstTime = first_time ? JSON.parse(first_time) : null;
    const parsedRedirectTo = redirect_to ? JSON.parse(redirect_to) : null;
    const parsedCreatePasswordToken = create_password_token
        ? JSON.parse(create_password_token)
        : null;
    if (parsedFirstTime) {
        localStorage.removeItem("first_time");
    }
    if (parsedRedirectTo) {
        localStorage.removeItem("redirect_to");
    }
    if (parsedCreatePasswordToken) {
        localStorage.removeItem("create_password_token");
    }
};
export const getAccountType = () => {
    if (typeof window !== "undefined") {
        const account = localStorage.getItem("accountType");
        const item = account ? JSON.parse(account) : null;
        return item;
    }
};
export const getPageType = (capitalize = false) => {
    if (typeof window !== "undefined") {
        const page = localStorage.getItem("page_type");
        const page_type = page ? JSON.parse(page) : null;
        if (page_type) {
            if (capitalize)
                return page_type.slice(0, 1).toUpperCase() + page_type.slice(1, page_type.length);
            return page_type;
        }
        else if (capitalize)
            return "Organization";
        return "organization";
    }
    else if (capitalize)
        return "Organization";
    return "organization";
};
export const clearFormStorage = () => {
    localStorage.removeItem(LOCAL_STORAGE_KEY);
    localStorage.removeItem(LOCAL_STORAGE_STEP_KEY);
};
export const setCandidateId = (ids = { account_id: '', representative_id: "", organization_id: '' }) => {
    localStorage.setItem("account_id", JSON.stringify(ids.account_id));
    localStorage.setItem("representative_id", JSON.stringify(ids.representative_id));
    localStorage.setItem("organization_id", JSON.stringify(ids.organization_id));
};
export const getToken = (tokenKey) => {
    const keyStructure = tokenKey.split('.');
    if (keyStructure.length === 2 &&
        ['localstorage', 'sessionstorage', 'cookie'].includes(keyStructure[0].toLowerCase())) {
        const storageType = keyStructure[0].toLowerCase();
        const key = keyStructure[1];
        if (storageType === 'localstorage') {
            const value = localStorage.getItem(key);
            try {
                return JSON.parse(value !== null && value !== void 0 ? value : '');
            }
            catch (_a) {
                return value;
            }
        }
        if (storageType === 'sessionstorage') {
            const value = sessionStorage.getItem(key);
            try {
                return JSON.parse(value !== null && value !== void 0 ? value : '');
            }
            catch (_b) {
                return value;
            }
        }
        if (storageType === 'cookie') {
            return getCookieValue(key);
        }
    }
    return null;
};
export const logoutUser = () => {
    try {
        // eslint-disable-next-line @typescript-eslint/no-var-requires, global-require
        require('./auth-refresh').cancelScheduledRefresh();
    }
    catch (_a) {
        /* module unavailable */
    }
    // VA-27781: the tokens are cookie-backed now; removing the localStorage copies
    // alone would leave the session alive.
    clearTokens();
    localStorage.removeItem("token");
    localStorage.removeItem("access");
    localStorage.removeItem("refresh");
    localStorage.removeItem("accountType");
    localStorage.removeItem("account_id");
    localStorage.removeItem("organization_id");
    localStorage.removeItem("page_type");
    localStorage.removeItem("organization_id");
    localStorage.removeItem("representative_id");
    sessionStorage.removeItem("organization_name");
    removeUserDetails();
    clearFormStorage();
};
export const getTokenDetails = (tokenKey) => {
    const keyStructure = tokenKey.split('.');
    if (keyStructure.length === 2 &&
        (keyStructure[0].toLocaleLowerCase() === 'localstorage' ||
            keyStructure[0].toLocaleLowerCase() === 'cookie')) {
        return {
            type: keyStructure[0].toLocaleLowerCase(),
            key: keyStructure[1],
        };
    }
    return null;
};
